import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import { usePwaInstall } from '../../hooks/usePwaInstall';

/**
 * Bottom-sheet style banner that invites the visitor to install the
 * PWA on their phone. Detection + trigger live in {@link usePwaInstall};
 * this component just renders the right copy depending on
 * platform and persists a 14-day dismiss flag in localStorage so it
 * doesn't keep nagging users who said "later".
 *
 * On iOS the banner instructs the user to use Share → Add to Home
 * Screen (no programmatic install API on Safari). On Android the
 * banner shows an Install button that calls the system install sheet.
 */
const DISMISS_KEY = 'afroespace.pwa.dismissedAt';
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

function readDismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode — fine, the banner just reappears next visit */
  }
}

export function InstallPwaPrompt() {
  const { t } = useTranslation();
  const { isStandalone, isIos, canInstall, install } = usePwaInstall();
  const [hidden, setHidden] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Already installed, or dismissed in the last 14 days → don't bug them.
    if (isStandalone || readDismissedRecently()) {
      setHidden(true);
      return;
    }
    // iOS path — small delay so the banner doesn't fight the first
    // paint and feels less spammy.
    if (isIos) {
      const t = window.setTimeout(() => setShowIosHint(true), 1500);
      return () => window.clearTimeout(t);
    }
  }, [isStandalone, isIos]);

  if (hidden || isStandalone) return null;
  const showAndroid = canInstall;
  if (!showAndroid && !showIosHint) return null;

  const handleDismiss = () => {
    markDismissed();
    setHidden(true);
  };

  const handleInstall = async () => {
    const accepted = await install();
    // Either way — hide the banner. If declined, respect the user for
    // 14 days. If accepted, the standalone flag from the hook will
    // unmount us on the next render too.
    if (!accepted) markDismissed();
    setHidden(true);
  };

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label={t('pwa.install.title')}
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 'auto' },
        right: { xs: 12, sm: 24 },
        bottom: { xs: 12, sm: 24 },
        maxWidth: 420,
        borderRadius: 3,
        zIndex: 1300,
        p: 1.75,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'common.white',
          }}
        >
          {showAndroid ? <GetAppRoundedIcon /> : <IosShareRoundedIcon />}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.3, mb: 0.25 }}>
            {t('pwa.install.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            {showAndroid ? t('pwa.install.androidHint') : t('pwa.install.iosHint')}
          </Typography>

          {showAndroid && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleInstall}
                sx={{
                  bgcolor: '#1A1816',
                  color: 'common.white',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#000', boxShadow: 'none' },
                }}
              >
                {t('pwa.install.installCta')}
              </Button>
              <Button size="small" onClick={handleDismiss} sx={{ color: 'text.secondary' }}>
                {t('pwa.install.later')}
              </Button>
            </Stack>
          )}
        </Box>

        <IconButton
          aria-label={t('common.close')}
          size="small"
          onClick={handleDismiss}
          sx={{ color: 'text.secondary' }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}
